import { useFormHandling } from '@pelckmans/business-components/hooks/useFormHandling';
import SelectField from '@pelckmans/elementis/components/select-field';
import TextField from '@pelckmans/elementis/components/text-field';
import { FormEventHandler, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { renameGroup } from '../../../api/MyGroupsApi';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import { SchoolYear } from '../../../context/MyGroupsContext';
import { Group } from '../../../../../api/groups';

type FormValues = {
  groupName: string;
};

type Props = {
  group: Group;
  editable: boolean;
  schoolYear: SchoolYear;
  onGroupRename: (groupWithNewName: Group) => void;
  onClose: () => void;
  formId: string;
};

function InfoContent({ group, editable, schoolYear, onGroupRename, onClose, formId }: Props) {
  const { t } = useTranslation();
  const handleError = useThrowAsyncError();

  const [submitted, setSubmitted] = useState(false);

  const validateForm = useCallback(
    (values: FormValues) => {
      const result: Record<string, string> = {};

      const { groupName } = values;

      if (editable && groupName?.length === 0) {
        result.groupName = t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.GROUP.ERROR');
      }

      return result;
    },
    [t, editable],
  );

  const initialFormValues: FormValues = useMemo(
    () => ({
      groupName: group.name,
    }),
    [group.name],
  );

  const { formValues, touched, errors, handleBlur, handleChange, isFormValid } = useFormHandling(
    initialFormValues,
    validateForm,
  );

  const handleSubmit: FormEventHandler<HTMLFormElement> = async e => {
    e.preventDefault();

    setSubmitted(true);

    if (!isFormValid) return;

    if (!touched.groupName) {
      onClose();

      return;
    }

    try {
      const groupWithNewName = await renameGroup(schoolYear, group.id, formValues.groupName);

      onGroupRename(groupWithNewName);

      onClose();
    } catch (err) {
      handleError(err);
    } finally {
      setSubmitted(false);
    }
  };

  return (
    <>
      <form noValidate id={formId} name={formId} onSubmit={handleSubmit} className="group-details-form">
        <span className="modal-content-title">{t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.TITLE')}</span>
        <div className="form-fields">
          <TextField
            id="groupName"
            name="groupName"
            labelText={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.GROUP.LABEL')}
            placeholder={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.GROUP.PLACEHOLDER')}
            required
            onChange={handleChange}
            onBlur={handleBlur}
            errorText={(touched.groupName || submitted) && errors.groupName ? errors.groupName : undefined}
            maxLength={25}
            disabled={!editable}
            value={formValues.groupName}
          />
          <SelectField
            id="schools"
            disabled
            required
            onChange={() => {}}
            value={group.school}
            renderValueContent={option => option.name}
            labelText={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SCHOOL.LABEL')}
            placeholder={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SCHOOL.PLACEHOLDER')}
            dropdownMenuProps={{
              ariaLabel: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SCHOOL.A11Y_LABEL'),
            }}
          />
          <SelectField
            id="subjects"
            disabled
            required
            multiple
            onChange={() => {}}
            value={group.subjects}
            renderValueContent={option => option}
            labelText={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SUBJECT.LABEL')}
            placeholder={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SUBJECT.PLACEHOLDER')}
            dropdownMenuProps={{
              ariaLabel: t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SUBJECT.A11Y_LABEL'),
            }}
            selectedValueLabel={t('PAGES.MY_GROUPS.MODALS.GROUP_DETAILS.TABS.INFO.BODY.FORM.SUBJECT.SELECTED_LABEL')}
          />
        </div>
      </form>
    </>
  );
}

export default InfoContent;
